/**
 * Default field definition for the ethics complaint form.
 *
 * Shape mirrors `agency-form.formFields` in Strapi: a list of sections, each
 * holding rows of inputs, where every input names the component to render
 * (`field`), what to bind to it (`propBind`) and how to validate it
 * (`validate`). Editing the CMS copy of this JSON changes the form without a
 * deploy; this constant is only the fallback when Strapi has nothing.
 *
 * `name` must match a key on ComplaintPayload — that is what the input binds
 * to. `confirmEmail` is the one exception: it is checked in the UI and never
 * sent to the API.
 */
export const DEFAULT_FORM_FIELDS = {
  fields: [
    {
      sectionTitle: "",
      sectionDescription: "",
      inputs: [
        [
          {
            name: "todaysDate",
            label: "Today's Date",
            field: "static-value",
            class: "col-md-6",
          },
          {
            name: "fullName",
            label: "Name of Person Filing Complaint",
            field: "b-form-input",
            class: "col-md-6",
            propBind: { placeholder: "Type your name here" },
            validate: {
              name: "fullName",
              required: true,
              veeValidate: "required",
              dataVvAs: "Name",
            },
          },
        ],
      ],
    },
    {
      sectionTitle: "How can we reach you?",
      sectionDescription:
        "We may need to contact you for further information. Please enter your contact details.",
      inputs: [
        [
          {
            name: "email",
            label: "Email Address",
            field: "b-form-input",
            class: "col-md-6",
            propBind: { type: "email", placeholder: "Type your email here" },
            validate: {
              name: "email",
              required: true,
              veeValidate: "required|email",
              dataVvAs: "Email address",
            },
          },
          {
            name: "confirmEmail",
            label: "Confirm Email Address",
            field: "b-form-input",
            class: "col-md-6",
            propBind: { type: "email", placeholder: "Confirm Email" },
          },
        ],
        [
          {
            name: "phoneNumber",
            label: "Phone Number",
            field: "b-form-input",
            class: "col-md-6",
            propBind: { placeholder: "Type your mobile number here" },
            validate: {
              name: "phoneNumber",
              required: true,
              veeValidate: "required",
              dataVvAs: "Phone number",
            },
          },
        ],
      ],
    },
    {
      sectionTitle: "Complaint Details",
      sectionDescription:
        "Please fill out the information below with as much detail as possible. You can upload any supporting documents or files below, if desired.",
      inputs: [
        [
          {
            name: "eventDate",
            label: "Event Date",
            field: "b-form-input",
            class: "col-md-6",
            propBind: { type: "date", placeholder: "Select Event Date" },
            validate: {
              name: "eventDate",
              required: true,
              veeValidate: "required",
              dataVvAs: "Event date",
            },
          },
        ],
        [
          {
            name: "againstName",
            label: "Who is the complaint against?",
            hint: "Please provide the name of the person you are filing a complaint against.",
            field: "b-form-input",
            class: "col-12",
            subClass: "half-width",
            propBind: { placeholder: "Name of the person" },
            validate: {
              name: "againstName",
              required: true,
              veeValidate: "required",
              dataVvAs: "Name of the person",
            },
          },
        ],
        [
          {
            name: "details",
            label: "Details of the complaint",
            hint: "Please describe the complaint with as much detail as possible and include any details pertinent to the investigation.",
            field: "b-form-textarea",
            class: "col-12",
            propBind: {
              rows: "6",
              placeholder: "Type details of the complaint here",
            },
            validate: {
              name: "details",
              required: true,
              veeValidate: "required",
              dataVvAs: "Complaint details",
            },
          },
        ],
      ],
    },
  ],
};
